import { Box, BoxProps, Stack, styled, Typography, useTheme } from '@mui/material'

import { FC, useMemo } from 'react'

import { TrailSystemStatusRing } from './TrailSystemStatusRing.component'

const RingsContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'mini',
})<BoxProps & { mini?: boolean }>(({ theme, mini }) => ({
  position: 'relative',
  zIndex: 1,
  [theme.breakpoints.down('xxs')]: {
    transform: mini ? undefined : 'scale(0.8)',
  },
}))

const RingsLabel = styled(Stack)(() => ({
  left: '50%',
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
}))

const FirmRing = styled(Box)(() => ({
  position: 'relative',
}))

const AbsoluteRing = styled(Box)(() => ({
  bottom: 0,
  left: 0,
  position: 'absolute',
  right: 0,
  top: 0,
}))

type TrailSystemStatusProps = {
  values: {
    lowImpact: number
    mediumImpact: number
    highImpact: number
    closed: number
    freezeThaw?: number
  }
  count: number
  mini?: boolean
  showFreezeThawRing?: boolean
}

export const TrailSystemStatus: FC<TrailSystemStatusProps> = (props) => {
  const theme = useTheme()

  const max = useMemo(() => {
    const values = [props.values.lowImpact, props.values.mediumImpact, props.values.highImpact, props.values.closed]
    if (props.showFreezeThawRing && props.values.freezeThaw) {
      values.push(props.values.freezeThaw)
    }
    return Math.max(...values)
  }, [props.values, props.showFreezeThawRing])

  return (
    <RingsContainer mini={props.mini}>
      <RingsLabel alignItems="center">
        <Typography variant="h3" fontWeight={700} fontSize={props.mini ? 32 : 48} lineHeight={1}>
          {props.count}
        </Typography>
        <Typography variant="body2" fontSize={props.mini ? 12 : 14}>
          trails
        </Typography>
      </RingsLabel>
      <AbsoluteRing>
        <TrailSystemStatusRing
          value={props.values.closed}
          label="Closed"
          size={140}
          scale={props.mini ? 0.75 : 1}
          total={props.count}
          color="#A0A0A0"
          max={max}
        />
      </AbsoluteRing>
      <AbsoluteRing>
        <TrailSystemStatusRing
          value={props.values.highImpact}
          label="High Impact"
          size={180}
          scale={props.mini ? 0.75 : 1}
          total={props.count}
          color={theme.palette.trailStatus.dark}
          max={max}
        />
      </AbsoluteRing>
      <AbsoluteRing>
        <TrailSystemStatusRing
          value={props.values.mediumImpact}
          label="Medium Impact"
          size={220}
          scale={props.mini ? 0.75 : 1}
          total={props.count}
          color={theme.palette.trailStatus.main}
          max={max}
        />
      </AbsoluteRing>
      {props.showFreezeThawRing ? (
        <>
          <AbsoluteRing>
            <TrailSystemStatusRing
              value={props.values.lowImpact}
              label="Low Impact"
              size={260}
              scale={props.mini ? 0.75 : 1}
              total={props.count}
              color={theme.palette.trailStatus.light}
              max={max}
            />
          </AbsoluteRing>
          <FirmRing>
            <TrailSystemStatusRing
              value={props.values.freezeThaw || 0}
              label="Freeze / Thaw"
              size={300}
              scale={props.mini ? 0.75 : 1}
              total={props.count}
              color={theme.palette.freezeThaw.main}
              max={max}
            />
          </FirmRing>
        </>
      ) : (
        <FirmRing>
          <TrailSystemStatusRing
            value={props.values.lowImpact}
            label="Low Impact"
            size={260}
            scale={props.mini ? 0.75 : 1}
            total={props.count}
            color={theme.palette.trailStatus.light}
            max={max}
          />
        </FirmRing>
      )}
    </RingsContainer>
  )
}
TrailSystemStatus.displayName = 'TrailSystemStatus'
